// Fetches a license JWT from the generateLicense callable.
//
// The token is short-lived (24h) and scoped to the serving origin. It is held
// in memory only — never persisted. Persisting it would outlive the deactivation
// window that the short TTL exists to enforce.
//
// Dev bypass: when running `npm run dev` (vite dev server) with a
// `VITE_DEV_LICENSE` in `.env.local`, that pre-minted localhost-scoped JWT is
// used directly instead of calling `generateLicense`. See TODO 0018 and
// `.env.example` for the mint one-liner. `import.meta.env.DEV` gates this at
// compile time, so production builds contain no codepath that reads a licence
// from an env var.

import { httpsCallable, type FunctionsError } from 'firebase/functions'
import { ref } from 'vue'

import { functions } from '../firebase'
import type { GenerateLicenseRequest, GenerateLicenseResponse } from '../types/callables'

export type LicenseFailure =
  | 'account-inactive'      // deactivated between the route guard and this call
  | 'origin-not-allowed'    // serving host is not on the function's allowlist
  | 'unauthenticated'       // session evaporated mid-flight
  | 'unavailable'           // network / cold start / transient
  | 'profile-not-assigned'  // requested profileId isn't in the caller's list — stale cache or race
  | 'profile-not-found'     // profile doc missing or malformed — admin deleted underneath
  | 'unknown'

export function licenseFailureMessage(kind: LicenseFailure): string {
  switch (kind) {
    case 'account-inactive':
      return 'Your account is no longer active. Contact your administrator.'
    case 'origin-not-allowed':
      // Only reachable via an unrecognised host — a misconfiguration, not user error.
      return 'Sketcher is not licensed to run on this address.'
    case 'unauthenticated':
      return 'Your session expired. Sign in again.'
    case 'unavailable':
      return 'Could not reach the licence server. Check your connection and reload.'
    case 'profile-not-assigned':
      // AppView is expected to catch this and recover by clearing the cached
      // choice and re-prompting; the message is a fallback if that fails.
      return 'The selected profile is no longer assigned to you. Reload to pick again.'
    case 'profile-not-found':
      // Fail-closed: admin deleted the profile between our picker load and our
      // licence fetch. Only path forward is to reload and pick a different one.
      return 'The selected profile no longer exists. Reload to pick a different one.'
    default:
      return 'Could not start Sketcher. Reload to try again.'
  }
}

/** Maps a Firebase callable error onto a LicenseFailure. */
function classify(e: unknown): LicenseFailure {
  const err = e as FunctionsError
  const code = err?.code ?? ''
  const msg = err?.message ?? ''

  if (code === 'functions/permission-denied') {
    // The function distinguishes these three by message; all arrive as
    // permission-denied, and each needs different UI recovery.
    if (msg.includes('origin-not-allowed'))    return 'origin-not-allowed'
    if (msg.includes('profile-not-assigned'))  return 'profile-not-assigned'
    return 'account-inactive'
  }
  if (code === 'functions/failed-precondition' && msg.startsWith('profile-')) {
    // profile-not-found:<id> and profile-malformed:<id> both fall here — the
    // caller cannot distinguish and both need the same "pick again" recovery.
    return 'profile-not-found'
  }
  if (code === 'functions/unauthenticated') return 'unauthenticated'
  if (code === 'functions/unavailable' || code === 'functions/deadline-exceeded') return 'unavailable'
  return 'unknown'
}

const callGenerateLicense = httpsCallable<GenerateLicenseRequest, GenerateLicenseResponse>(
  functions,
  'generateLicense',
)

/**
 * Extract `exp` (unix seconds) from a JWT's payload without verifying the
 * signature. Returns null if the token is not shaped like a JWT or the payload
 * has no numeric `exp`. Dev-bypass only — the engine's validator still checks
 * the signature and exp itself.
 */
function decodeDevLicenseExp(jwt: string): number | null {
  const parts = jwt.split('.')
  const payloadB64 = parts[1]
  if (parts.length !== 3 || !payloadB64) {
    console.error('[useLicense] VITE_DEV_LICENSE is not a JWT (expected header.payload.signature)')
    return null
  }
  try {
    const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
    const payload = JSON.parse(atob(b64 + pad)) as { exp?: unknown }
    if (typeof payload.exp !== 'number') {
      console.error('[useLicense] VITE_DEV_LICENSE payload has no numeric `exp`')
      return null
    }
    return payload.exp
  } catch (e) {
    console.error('[useLicense] VITE_DEV_LICENSE payload is malformed:', e)
    return null
  }
}

export function useLicense() {
  const license   = ref<string | null>(null)
  const expiresAt = ref<string | null>(null)
  const failure   = ref<LicenseFailure | null>(null)

  /**
   * Fetch a licence, optionally scoped to a capability profile.
   *
   * `profileId` is only sent when the user has 1+ profiles assigned and the
   * caller has resolved which one to embed (either the picker's choice, a
   * cached prior choice, or the sole option). Omit for unrestricted users;
   * the callable protocol serialises an explicit `undefined` as `null`, which
   * the server would then reject as "profile-not-assigned".
   */
  async function fetchLicense(profileId?: string): Promise<string | null> {
    failure.value = null

    if (import.meta.env.DEV && import.meta.env.VITE_DEV_LICENSE) {
      const jwt = import.meta.env.VITE_DEV_LICENSE
      const exp = decodeDevLicenseExp(jwt)
      if (exp == null) {
        // Present but unparseable: surface rather than falling through, so the
        // developer fixes the env var instead of getting a misleading callable
        // error. Absent (the common case on a fresh machine) still falls
        // through to the callable and produces the honest `origin-not-allowed`.
        failure.value = 'unknown'
        license.value = null
        return null
      }
      // The dev licence carries no capabilities claim by design; profile
      // selection is a no-op in dev. Left in the API for symmetry so AppView
      // doesn't have to special-case DEV.
      license.value   = jwt
      expiresAt.value = new Date(exp * 1000).toISOString()
      return jwt
    }

    try {
      const payload: GenerateLicenseRequest = profileId ? { profileId } : {}
      const { data } = await callGenerateLicense(payload)
      license.value   = data.license
      expiresAt.value = data.expiresAt
      return data.license
    } catch (e) {
      failure.value = classify(e)
      license.value = null
      return null
    }
  }

  return { license, expiresAt, failure, fetchLicense }
}
